import { TIconName } from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import {
  MouseEventHandler,
  ReactNode,
  useRef,
  type ComponentPropsWithRef,
  type JSX,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import Divider from '../Divider/Divider';
import RawInput from '../Input/RawInput';
import DropdownGroup from './DropdownGroup';
import DropdownGroupLabel from './DropdownGroupLabel';

export type TDropdownItem = {
  icon?: TIconName;
  label: string | JSX.Element;
  onClick: MouseEventHandler<HTMLButtonElement>;
  searchKey?: string;
  selected?: boolean;
  disabled?: boolean;
  disabledReasons?: string[];
  isProcessing?: boolean;
};

export type TDropdownSection = {
  heading?: string | JSX.Element;
  items: TDropdownItem[];
};

export interface IProps extends ComponentPropsWithRef<'div'> {
  items: TDropdownItem[] | TDropdownSection[];
  hasSearch?: boolean;
  renderItem: (item: TDropdownItem, index: number) => ReactNode;
  footer?: ReactNode;
}

/**
 * Pure dropdown content renderer without HeadlessUI dependency.
 * Use DropdownMenuItems for the full HeadlessUI integration.
 */
const DropdownCore = (props: IProps) => {
  const {
    className,
    items,
    hasSearch = false,
    renderItem,
    footer,
    ...rest
  } = props;

  const formMethods = useForm<{
    search: string;
  }>();
  const { control, watch } = formMethods;
  // Keep track of the last input to focus when dropdown opens
  const lastInputRef = useRef<HTMLInputElement | null>(null);

  const searchValue = watch('search', '');

  // Normalize items to always be an array of sections
  const isSectioned =
    items.length > 0 &&
    typeof items[0] === 'object' &&
    'items' in items[0] &&
    Array.isArray(items[0].items);

  const sections: TDropdownSection[] = isSectioned
    ? (items as TDropdownSection[])
    : [{ items: items as TDropdownItem[] }];

  const filteredSections = hasSearch
    ? sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => {
            if (item.searchKey) {
              return item.searchKey
                .toLowerCase()
                .includes(searchValue.toLowerCase());
            }

            if (typeof item.label === 'string') {
              return item.label
                .toLowerCase()
                .includes(searchValue.toLowerCase());
            }

            console.error(
              "Dropdown item is missing 'searchKey' property and can't be filtered by a JSX `label`."
            );

            return true;
          }),
        }))
        .filter((section) => section.items.length > 0)
    : sections;

  return (
    <div
      className={clstx(
        // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Limits the transition to these properties
        'typography:prose z-dropdown duration mt-2 min-w-44 rounded-lg bg-white border border-gray-200 shadow-md transition-[opacity,margin]',
        className
      )}
      {...rest}
    >
      <div className="p-2">
        {hasSearch && (
          <Controller
            control={control}
            name="search"
            render={({ field: { onChange, ref, ...rest } }) => {
              return (
                <RawInput
                  ref={(node) => {
                    ref(node);
                    // Focus the input only when the dropdown opens --> not on every render to avoid scroll jump
                    if (node && node !== lastInputRef.current) {
                      lastInputRef.current = node;
                      requestAnimationFrame(() => node.focus());
                    }
                  }}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    // https://github.com/tailwindlabs/headlessui/issues/1758
                    if (e.key === ' ' || e.code === 'Space') {
                      e.stopPropagation();
                    }
                  }}
                  className="mb-2"
                  type="search"
                  placeholder="Search..."
                  sizeVariant="sm"
                  autoComplete="off"
                  {...rest}
                />
              );
            }}
          />
        )}

        {filteredSections.map((section, sectionIndex) => {
          const offset = filteredSections
            .slice(0, sectionIndex)
            .reduce((acc, s) => acc + s.items.length, 0);

          return (
            <DropdownGroup key={sectionIndex}>
              {section.heading && (
                <DropdownGroupLabel>{section.heading}</DropdownGroupLabel>
              )}

              {section.items.map((item, itemIndex) =>
                renderItem(item, offset + itemIndex)
              )}

              {/* divider */}
              {sectionIndex < filteredSections.length - 1 && (
                <Divider className="my-2" />
              )}
            </DropdownGroup>
          );
        })}
      </div>

      {footer && (
        <>
          <Divider />

          <div>{footer}</div>
        </>
      )}
    </div>
  );
};

export default DropdownCore;
